import React from 'react';
import { GoogleIcon, MetaIcon, WhatsAppIcon } from '../../components';
import { Youtube, Mail, Facebook, Instagram, Lock } from 'lucide-react';
import MetaAssetsLoadingState from './MetaAssetsLoadingState';

// Tarjeta genérica con el mismo estilo visual que PlatformCard
const GradientCard = ({ title, description, icon, onClick, notice, isSelected, disabled = false, blockedReason, footerIcon, footerIcons, badge }) => {
  const handleClick = () => {
    if (disabled) return;
    if (onClick) onClick();
  };
  const isBlocked = Boolean(disabled && blockedReason);
  const bottomIcons = footerIcons || (footerIcon ? [footerIcon] : []);
  return (
    <button
      type="button"
      aria-disabled={disabled}
      onClick={handleClick}
      className={`group relative w-full h-auto min-h-[160px] lg:min-h-[190px] text-center rounded-2xl p-3 lg:p-4 transition-all duration-500 ease-out overflow-hidden ${
        isSelected ? 'ring-2 ring-primary/60 ring-offset-2 ring-offset-slate-900' : ''
      } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      style={{
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(0, 0, 0, 0.1)'
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
          e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
          e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.08), 0 0 40px rgba(15, 49, 247, 0.1)';
          e.currentTarget.style.backdropFilter = 'blur(30px)';
          e.currentTarget.style.WebkitBackdropFilter = 'blur(30px)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)';
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.backdropFilter = 'blur(20px)';
        e.currentTarget.style.WebkitBackdropFilter = 'blur(20px)';
      }}
    >
      {/* Gradiente de brillo superior */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-60 transition-opacity duration-500 group-hover:opacity-100"></div>
      
      {/* Gradiente sutil de fondo */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"></div>
      
      <div className={`flex flex-col items-center justify-center relative z-10 h-full min-h-[160px] lg:min-h-[190px] ${bottomIcons.length > 0 ? 'pb-8' : ''} ${isBlocked ? '' : ''}`}>
        <div className="flex flex-col items-center justify-center">
          {icon && (
            <div className="bg-opacity-0 mb-1 lg:mb-2 inline-flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-2xl transform transition-transform duration-500 group-hover:scale-110">
              {icon}
            </div>
          )}
          {badge && (
            <div className="mb-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 border border-blue-400/30 backdrop-blur-md transition-all duration-300 shadow-lg shadow-blue-500/10">
              {badge}
            </div>
          )}
          <div className="text-white font-semibold tracking-tight transition-colors duration-300 group-hover:text-white text-center">{title}</div>
          {description && (
            <div className="text-sm text-white/70 mt-0.5 leading-snug max-w-[28ch] mx-auto transition-colors duration-300 group-hover:text-white/80 text-center">{description}</div>
          )}
        </div>
        {bottomIcons.length > 0 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 text-white/60 pointer-events-none transition-all duration-300 group-hover:text-white/80 group-hover:gap-2.5">
            {bottomIcons.map((ic, idx) => (
              <div key={idx} className="w-6 h-6 bg-opacity-0 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                {ic}
              </div>
            ))}
          </div>
        )}
      </div>

      {isBlocked ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-20">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-orange-900/50 border-2 border-orange-500/50 shadow-lg backdrop-blur-md flex flex-col items-center justify-center">
            <Lock className="w-5 h-5 text-orange-300" />
            <span className="text-[10px] font-semibold text-orange-200 mt-0.5 px-1 text-center leading-tight">{blockedReason}</span>
          </div>
        </div>
      ) : notice ? (
        <div className="mt-3 text-left relative z-10">
          <div className="flex items-start gap-2 text-xs text-white/80">
            <span className="flex-1">{notice.text}</span>
            {notice.onClick && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); notice.onClick && notice.onClick(); }}
                className="text-blue-300 hover:text-blue-200 underline whitespace-nowrap transition-colors"
              >
                {notice.ctaLabel || 'Configurar ahora'}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </button>
  );
};

// Tarjeta de plataformas (mismo estilo visual)
const PlatformCard = ({ title, description, icon, onClick, footerIcons }) => (
  <GradientCard title={title} description={description} icon={icon} onClick={onClick} footerIcons={footerIcons} />
);

/**
 * Step 1: Platform Selection Component
 * 
 * This component renders the platform selection step of the campaign wizard.
 * Users can choose between Google Ads, Meta Ads, WhatsApp, or all platforms.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onPlatformSelect - Callback function when a platform is selected
 * @returns {JSX.Element} Platform selection interface
 */
const Step1PlatformSelection = ({ onPlatformSelect, isMetaTransitioning = false, transitioningPlatform = 'meta' }) => {
  return (
    <div className="relative">
      {isMetaTransitioning && (
        <div className="absolute inset-0 z-20 pointer-events-auto">
          <MetaAssetsLoadingState platform={transitioningPlatform} />
        </div>
      )}
      <div className="space-y-4 min-h-[40vh] lg:min-h-[46vh] flex items-start lg:items-center">
        <div className="max-w-4xl mx-auto w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
            <PlatformCard
              title="Google Ads"
              description={null}
              icon={<GoogleIcon className="h-5 w-5 lg:h-6 lg:w-6" />}
              footerIcons={[<GoogleIcon key="g" className="w-3 h-3 lg:w-3.5 lg:h-3.5" variant="mono" />, <Youtube key="yt" className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-white/60" />, <Mail key="gm" className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-white/60" />]}
              onClick={() => onPlatformSelect('google')}
            />

            <PlatformCard
              title="Meta Ads"
              description={null}
              icon={<MetaIcon className="h-5 w-5 lg:h-6 lg:w-6" />}
              footerIcons={[<Facebook key="fb" className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-white/60" />, <Instagram key="ig" className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-white/60" />]}
              onClick={() => onPlatformSelect('meta')}
            />

            <PlatformCard
              title="WhatsApp"
              description={null}
              icon={<WhatsAppIcon className="h-5 w-5 lg:h-6 lg:w-6" />}
              footerIcons={[<Facebook key="fb" className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-white/60" />, <Instagram key="ig" className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-white/60" />]}
              onClick={() => onPlatformSelect('whatsapp')}
            />

            <PlatformCard
              title="Todas las campañas"
              description={null}
              icon={<span className="inline-flex h-5 w-5 lg:h-6 lg:w-6 items-center justify-center rounded bg-gradient-to-br from-white/20 to-white/10 text-white/90 backdrop-blur-sm border border-white/20 shadow-sm">*</span>}
              onClick={() => onPlatformSelect('all')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1PlatformSelection;
